import { useEffect } from 'react';
import { logWithTakt } from '../utils/logWithTakt';

/**
 * Which A3C embedded-chat toggle emitted the event. Both toggles drive the
 * identical embedded-chat Redux state; `source` is the only axis that tells
 * them apart downstream, so it is the one field the event name does NOT encode.
 */
export type AdsAgentToggleSource = 'sidenav' | 'focus-header';

// Takt custom-event contexts for the A3C embedded-chat toggle. Shared by both
// toggle variants (sidenav + focus-header) so they report under the same
// events. The event name already encodes open vs close, so the click payload
// carries only `source` — no redundant `action` field.
export const ADS_AGENT_TOGGLE_SHOWN_EVENT = 'ads_agent_toggle_shown';
export const ADS_AGENT_TOGGLE_OPEN_EVENT = 'ads_agent_toggle_open';
export const ADS_AGENT_TOGGLE_CLOSE_EVENT = 'ads_agent_toggle_close';

// Module-level (not a per-instance ref) so "once per page load" holds even
// across remounts. Both toggles are conditionally mounted, so a component-local
// ref dies with the instance and a remount would re-fire `shown`; a flag on the
// module — evaluated once per bundle load — does not. React 17 (this package's
// pinned major) does not double-invoke mount effects, so this is the only guard
// that actually changes behaviour.
let hasLoggedShown = false;

/**
 * Takt instrumentation for an A3C embedded-chat toggle. Owns the one-time
 * `shown` impression effect (fired once per page load) and returns a click
 * logger that emits the open/close event for the given `source`.
 *
 * @param source which toggle variant is mounting the hook.
 * @returns a click logger; call it with the toggle's NEXT open state.
 */
export const useAdsAgentToggleTakt = (
  source: AdsAgentToggleSource,
): ((nextOpen: boolean) => void) => {
  useEffect(() => {
    if (hasLoggedShown) return;
    hasLoggedShown = true;
    logWithTakt(ADS_AGENT_TOGGLE_SHOWN_EVENT, { source });
  }, [source]);

  return (nextOpen: boolean) => {
    logWithTakt(
      nextOpen ? ADS_AGENT_TOGGLE_OPEN_EVENT : ADS_AGENT_TOGGLE_CLOSE_EVENT,
      { source },
    );
  };
};

/**
 * Test-only: resets the module-level "once per page load" flag so each test can
 * model a fresh page load. Not for production use.
 * @internal
 */
export const __resetAdsAgentToggleTaktForTests = (): void => {
  hasLoggedShown = false;
};

export default useAdsAgentToggleTakt;
